import React from 'react'

import Badge from '@mui/material/Badge'
import IconButton from '@mui/material/IconButton'
import { useGridApiContext, useGridRootProps } from '@mui/x-data-grid-pro'
import type { GridSortDirection } from '@mui/x-data-grid-pro'

import { styled } from '@mui/material/styles'

const IconButtonContainer = styled('div')({
  display: 'flex',
  visibility: 'hidden',
  width: 0,
})

interface AccessibleSortIconProps {
  field: string
  direction: GridSortDirection
  index: number | undefined
  sortingOrder: readonly GridSortDirection[]
  disabled?: boolean
  className?: string
}

function AccessibleSortIconRaw({
  direction,
  index,
  sortingOrder,
  disabled,
  className,
  field,
}: AccessibleSortIconProps) {
  const apiRef = useGridApiContext()
  const rootProps = useGridRootProps()

  let Icon: React.JSXElementConstructor<any> | null = null
  const iconProps: Record<string, unknown> = {}

  if (direction === 'asc') {
    Icon = rootProps.slots.columnSortedAscendingIcon
  } else if (direction === 'desc') {
    Icon = rootProps.slots.columnSortedDescendingIcon
  } else {
    Icon = rootProps.slots.columnUnsortedIcon ?? null
    iconProps.sortingOrder = sortingOrder
  }

  if (!Icon) return null

  const column = apiRef.current.getColumn(field)
  const columnName = column?.headerName ?? field

  let sortStateLabel = ''
  if (direction === 'asc') sortStateLabel = ', sorted ascending'
  else if (direction === 'desc') sortStateLabel = ', sorted descending'

  const sortTypeLabel = !['Total', 'Category'].includes(columnName) ? ' category totals' : ''
  const ariaLabel = `Sort by ${columnName}${sortTypeLabel}${sortStateLabel}`

  const iconElement = <Icon className="MuiDataGrid-sortIcon" fontSize="small" {...iconProps} />

  const iconButton = (
    <IconButton
      disabled={disabled}
      size="small"
      tabIndex={-1}
      {...(rootProps.slotProps?.baseIconButton as Record<string, unknown>)}
      aria-label={ariaLabel}
      className={`MuiDataGrid-sortButton${className ? ` ${className}` : ''}`}
      title={ariaLabel}
    >
      {iconElement}
    </IconButton>
  )

  return (
    <IconButtonContainer className="MuiDataGrid-iconButtonContainer">
      {index != null ? (
        <Badge badgeContent={index} color="default" overlap="circular">
          {iconButton}
        </Badge>
      ) : (
        iconButton
      )}
    </IconButtonContainer>
  )
}

export const AccessibleSortIcon = React.memo(AccessibleSortIconRaw)
